import { useRef, useState } from 'react';
import { Box, styled } from '@material-ui/core';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { SRoundedContainer } from '@setvi/shared/components';
import { Customer } from 'Services/MongoRealm/Types';
import {
  PriceGroup,
  Product,
  ProductDetailsTabsIds
} from '@setvi/shared/interfaces';

import { RealmProvider } from 'Providers/MongoRealmProvider';
import { useCatalogConfig } from 'pages/products/hooks/useCatalogConfig';
import { InvalidConfiguration } from 'pages/products/components/invalid-configuration';
import { CustomerPreferencesInterface } from 'componentsV2/customer-preferences/interfaces';

import { distances } from '@setvi/shared/styles';

import { Footer, Header, Tabs } from './components';
import {
  AssociatedProducts,
  AssociatedResource,
  OrderHistory,
  ProductInfo,
  SalesEnablement,
  SimilarProducts,
  Warehouse,
  ProductEnhancer,
  ProductAssistant
} from './subpages';

const TabContentWrapper = styled(Box)({
  display: 'flex',
  overflow: 'hidden',
  padding: distances.large,
  paddingTop: 8,
  flex: 1
});

const Container = styled(SRoundedContainer)({
  width: '100%',
  overflow: 'hidden auto'
});

export interface DetailsContentProps {
  product: Product;
  selectedProducts?: Product[];
  selectedCustomer?: Customer;
  defaultOpenedTab?: ProductDetailsTabsIds;
  client: any;
  canSelectProducts?: boolean;
  readOnly?: boolean;
  hideProductPrice?: boolean;
  orderHistoryValues?: {
    accountId: string;
    addressId: string;
  };
  selectedPriceGroup?: PriceGroup;
  customerPreferences?: CustomerPreferencesInterface;
  priceLoading?: boolean;
  handleProductSelect?: (isChecked: boolean, product: Product[]) => void;
  updateProduct?: (product: Product, props: Partial<Product>) => void;
  captureResourceAnalytics?: () => void;
  openProductDetails?: (
    product: Product,
    defaultOpenedTab?: ProductDetailsTabsIds
  ) => void;
  closeDialog?: () => void;
  handleEmail?: () => void;
  handleSelected?: () => void;
  handleCloseActionBar?: () => void;
}

export const DetailsContent = ({
  product,
  selectedProducts,
  selectedCustomer,
  defaultOpenedTab = ProductDetailsTabsIds.PRODUCT_INFO,
  client,
  canSelectProducts,
  priceLoading,
  hideProductPrice = false,
  readOnly = false,
  orderHistoryValues,
  customerPreferences,
  selectedPriceGroup,
  handleProductSelect,
  updateProduct,
  captureResourceAnalytics,
  openProductDetails,
  closeDialog,
  handleEmail,
  handleSelected,
  handleCloseActionBar
}: DetailsContentProps) => {
  const flags = useFlags();

  const [activeTab, setActiveTab] =
    useState<ProductDetailsTabsIds>(defaultOpenedTab);
  const { configuration, invalidConfiguration } = useCatalogConfig();

  const parentRef = useRef<HTMLDivElement>(null);
  const tabs = configuration.productDetails?.tabs || [];

  if (invalidConfiguration) return <InvalidConfiguration />;

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        flex={1}
        minHeight={0}
        overflow="hidden auto"
        // @ts-ignore
        ref={parentRef}>
        {/* {product?.categories?.[0]?.breadcrumbs && (
          <Box
            style={{ padding: `${distances.px.base} ${distances.px.large}` }}>
            <SBreadcrumbs<ProductCategoryBreadcrumb>
              {...(!readOnly && {
                onBreadcrumbClick: breadcrumb =>
                  window.open(
                    `${ROUTES.PRODUCTS_LIST_VIEW}&categoryId=${breadcrumb?._id}`,
                    '_blank'
                  )
              })}
              parseBreadcrumb={breadcrumb => breadcrumb?.name}
              items={product?.categories?.[0]?.breadcrumbs}
              fontWeight={400}
            />
          </Box>
        )} */}
        {/* <Grid
          container
          spacing={3}
          style={{ margin: 0, width: '100%', padding: '0 8px' }}>
          <Grid item xs={4}>
            <SGallery
              onImageClick={img => {
                product.images.length > 0 && setSelectedImage(img);
              }}
              images={images}
            />
          </Grid>
          <Grid item xs={8}>
            <ProductShowcase
              product={product}
              priceLoading={priceLoading}
              selectedCustomer={selectedCustomer}
              hideProductPrice={hideProductPrice}
              client={client}
              priceType={configuration.price}
              productDetails={configuration.productDetails}
              updateProduct={updateProduct}
              onStockClick={() => setActiveTab(ProductDetailsTabsIds.WAREHOUSE)}
            />
          </Grid>
        </Grid>
        <Divider />
          */}
        <Box px={4} py={3} display="flex" justifyContent="space-between">
          <Header
            product={product}
            selectedCustomer={selectedCustomer}
            selectedPriceGroup={selectedPriceGroup}
            hideProductPrice={hideProductPrice}
            client={client}
            priceLoading={priceLoading}
            pricing={configuration?.productDetails?.pricing}
            priceType={configuration.price}
            productDetails={configuration.productDetails}
            closeDetailsDialog={closeDialog}
            updateProduct={updateProduct}
            onStockClick={() => setActiveTab(ProductDetailsTabsIds.WAREHOUSE)}
          />
        </Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          pr={2}>
          <Tabs
            currentTab={activeTab}
            tabs={tabs}
            onTabChange={setActiveTab}
            orderHistoryValues={!!orderHistoryValues}
          />

          {/* <Box display="flex" alignItems="center" gridGap={8} mr="16px">
            {flags?.productAssistant && (
              <SButton
                variant="outlined"
                startIcon={<AIIcon />}
                style={{
                  borderRadius: 36
                }}
                onClick={() => {
                  openDialog(
                    <SAIDIalogContainer
                      width="100%"
                      height="100%"
                      headerDivider={false}
                      title="AI Product Assistant"
                      description="You can Ask questions about the Product, you can also get overview and also get FAQ for this Product.">
                      <AIContentBox>
                        <AskProduct product={product} />
                      </AIContentBox>
                    </SAIDIalogContainer>,
                    {
                      id: 'product-chat',
                      preventClose: true,
                      dialogClasses: { paper: dialogPaper }
                    }
                  );
                }}>
                Ask AI
              </SButton>
            )}
          </Box> */}
        </Box>

        {activeTab === ProductDetailsTabsIds.PRODUCT_INFO && (
          <TabContentWrapper>
            <ProductInfo product={product} client={client} />
          </TabContentWrapper>
        )}
        {activeTab === ProductDetailsTabsIds.WAREHOUSE && (
          <TabContentWrapper>
            <Container>
              <Warehouse
                product={product}
                selectedCustomer={selectedCustomer}
                configColumns={
                  configuration.productDetails.tabs.find(
                    i => i.id === ProductDetailsTabsIds.WAREHOUSE
                  )?.columns
                }
              />
            </Container>
          </TabContentWrapper>
        )}
        {activeTab === ProductDetailsTabsIds.ASSOCIATED_RESOURCE && (
          <TabContentWrapper>
            <Container display="flex">
              <AssociatedResource
                product={product}
                readOnly={readOnly}
                captureResourceAnalytics={captureResourceAnalytics}
                closeDialog={closeDialog}
              />
            </Container>
          </TabContentWrapper>
        )}
        {activeTab === ProductDetailsTabsIds.ASSOCIATED_PRODUCTS && (
          <RealmProvider>
            <TabContentWrapper>
              <Container display="flex">
                <AssociatedProducts
                  product={product}
                  selectedProducts={selectedProducts}
                  canSelectProducts={canSelectProducts}
                  readOnly={readOnly}
                  handleProductSelect={handleProductSelect}
                  openProductDetails={openProductDetails}
                  closeDialog={closeDialog}
                  handleEmail={handleEmail}
                  handleSelected={handleSelected}
                  handleCloseActionBar={handleCloseActionBar}
                />
              </Container>
            </TabContentWrapper>
          </RealmProvider>
        )}
        {activeTab === ProductDetailsTabsIds.SIMILAR_PRODUCTS && (
          <TabContentWrapper>
            <Container display="flex">
              <SimilarProducts
                product={product}
                selectedProducts={selectedProducts}
                canSelectProducts={canSelectProducts}
                scrollParentRef={parentRef}
                closeDialog={closeDialog}
                readOnly={readOnly}
                openProductDetails={openProductDetails}
                handleProductSelect={handleProductSelect}
                selectedCustomer={selectedCustomer}
                handleEmail={handleEmail}
                handleSelected={handleSelected}
                handleCloseActionBar={handleCloseActionBar}
              />
            </Container>
          </TabContentWrapper>
        )}
        {activeTab === ProductDetailsTabsIds.SALES_ENABLEMENT &&
          flags?.salesEnablement && (
            <TabContentWrapper>
              <SalesEnablement product={product} />
            </TabContentWrapper>
          )}

        {activeTab === ProductDetailsTabsIds.ORDER_HISTORY && (
          <TabContentWrapper>
            <Container>
              <OrderHistory productId={product._id} {...orderHistoryValues} />
            </Container>
          </TabContentWrapper>
        )}

        {activeTab === ProductDetailsTabsIds.PRODUCT_ENHANCER &&
          flags?.productEnhancer && (
            <TabContentWrapper>
              <ProductEnhancer product={product} />
            </TabContentWrapper>
          )}

        {activeTab === ProductDetailsTabsIds.PRODUCT_ASSISTANT &&
          flags?.productAssistant && (
            <TabContentWrapper>
              <ProductAssistant
                product={product}
                customerPreferences={customerPreferences}
              />
            </TabContentWrapper>
          )}

        <Footer
          product={product}
          readOnly={readOnly}
          selectedProducts={selectedProducts}
          handleProductSelect={handleProductSelect}
          canSelectProducts={canSelectProducts}
        />
      </Box>
    </>
  );
};
